import { DateTime } from 'luxon'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useState } from 'react'

import Alert from '@Icons/alert-icon.svg?react'
import ArrowRed from '@Icons/arrow-down-red.svg?react'
import ArrowUp from '@Icons/arrow-up-green.svg?react'
import Clock from '@Icons/clock-fast-forward.svg?react'
import Flag from '@Icons/flag.svg?react'
import HelpIcon from '@Icons/help-icon.svg?react'
import PauseIcon from '@Icons/pause-circle.svg?react'
import Pencil from '@Icons/pencil.svg?react'
import PlayIcon from '@Icons/play-circle.svg?react'
import Rubbish from '@Icons/rubbish-icon.svg?react'
import TickGreen from '@Icons/tick-green.svg?react'
import XRed from '@Icons/x-red.svg?react'
import {
  useMeCompetitorPreferencesQuery,
  useMePreferencesQuery,
  useMeQuery,
} from '@Store/api'
import { userIsChannelBasedSelector } from '@Store/api/auth'
import { useEditUrlMutation, useLazyGetReportQuery } from '@Store/api/product'
import { useAppSelector } from '@Store/hooks'
import PrimaryButton from '@Utils/buttons/primary-button'
import {
  NEW_URL_CONTROLLING_STATUS,
  UNQUEUED_URL_CONTROLLING_STATUS,
} from '@Utils/constants'
import { useAlreadyReportedUrlDispatch } from '@Utils/context/modal/UseAlreadyReportedUrl'
import { useDebugMode } from '@Utils/context/modal/useDebugMode'
import { useRemoveUrlDispatch } from '@Utils/context/modal/useRemoveUrl'
import { useReportUrlDispatch } from '@Utils/context/modal/useReportUrl'
import { useToggleExclusionDispatch } from '@Utils/context/modal/useToggleExclusion'
import {
  engineCycles,
  feedCycles,
  findNearestCycle,
} from '@Utils/helpers/cycle'
import {
  ENGINE_URL_FALLBACK_NOTE,
  isTotalPriceAvailable,
} from '@Utils/helpers/total-price'
import TextInput from '@Utils/inputs/text-input'
import Tooltip from '@Utils/tool-tip'
import getDecodedURL from '@Utils/url/getDecodedURL'

export default function TrackerRow({
  tracker,
  position,
  additionalCost,
  productId,
  setEditing,
  isEditing,
  setWarning,
  showTotalPrice,
}: {
  tracker?: any
  position?: any
  additionalCost?: number
  productId?: number
  setEditing?: any
  isEditing: number | null
  showTotalPrice?: boolean
  setWarning: Dispatch<SetStateAction<string | undefined>>
}): JSX.Element {
  const { data: mePreferencesData, isSuccess } = useMePreferencesQuery()

  const { isDebug } = useDebugMode()

  const {
    open: openRemoveUrlModal,
    handleApply: onRemoveUrlConfirm,
    deleteUrl,
  } = useRemoveUrlDispatch()

  const {
    open: openReportUrlModal,
    close: closeReportUrlModal,
    setUrlInfo,
    resetReportUrl,
  } = useReportUrlDispatch()

  const { open: openAlreadyReportedModal, close: closeAlreadyReportedModal } =
    useAlreadyReportedUrlDispatch()

  const {
    open: openToggleExclusionModal,
    handleApply: onToggleExclusionConfirm,
    toggleExclusion,
  } = useToggleExclusionDispatch()

  const { data: competitorPreferencesData } = useMeCompetitorPreferencesQuery(
    undefined,
    {
      skip: mePreferencesData?.monitoring_source === 'PRISYNC_ENGINE',
    },
  )
  const [reportStatus, { data, isLoading, isError, isFetching }] =
    useLazyGetReportQuery()

  const createRequest: any = {
    id: productId,
    urlId: tracker?.id,
  }

  const [date, setDate] = useState(DateTime.now())
  const [newValue, setNewValue] = useState('')
  const [editUrl, {}] = useEditUrlMutation()

  const { data: meData } = useMeQuery()

  const companyIsChannelBased = useAppSelector(userIsChannelBasedSelector)

  // WEG-1156: A Shopify user's own (is_mine) URL is synced from Shopify.
  // Deleting it forces a manual delete-and-reupload of the product, so the
  // Remove action must be hidden for it — mirroring channel-feed URLs.
  const isShopifyOwnUrl =
    Boolean(tracker?.is_mine) && meData?.isShopify !== false
  const handleRemoveUrlClick = (): void => {
    openRemoveUrlModal()
    onRemoveUrlConfirm(() => {
      deleteUrl({
        id: productId,
        urlId: tracker?.id,
      })
    })
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setDate(DateTime.now())
    }, 60000)

    return () => {
      clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    if (isFetching) {
      closeAlreadyReportedModal()
      closeReportUrlModal()
      return
    }
    if (data === true) {
      openAlreadyReportedModal()
    } else if (data === false) {
      resetReportUrl()
      setUrlInfo({ id: productId as number, urlId: tracker?.id })
      openReportUrlModal()
    }
  }, [data, isFetching])

  let debugTracker = ''
  if (isDebug) {
    debugTracker = Object.entries(tracker).join('\n').replaceAll(',', ': ')
  }

  const onUpdate = async () => {
    try {
      const response = await editUrl({
        id: productId,
        urlId: tracker?.id,
        url: newValue,
      }).unwrap()
      if (response?.warning) {
        setWarning(response.warning.message)
      }
      setEditing(null)
    } catch (ex) {}
  }

  const calculateIsmineUrlDateUpdated = (
    urlDateUpdated: string,
    urlDateCreated: string,
  ): string | null => {
    // https://prisync.atlassian.net/browse/WEG-1572
    if (meData?.register_source !== 'Shopify') {
      return DateTime.fromISO(urlDateUpdated).toRelative()
    }

    const baseDate = urlDateUpdated ? urlDateUpdated : urlDateCreated

    const userTimezone = meData?.timezone
    const nowForUser = DateTime.now().setZone(userTimezone)
    const cycleFrequency = companyIsChannelBased ? '1' : meData.cycle_frequency
    const cycles = companyIsChannelBased ? feedCycles : engineCycles

    return DateTime.max(
      DateTime.fromISO(baseDate).setZone(userTimezone),
      findNearestCycle(nowForUser, userTimezone, cycleFrequency, cycles),
    ).toRelative()
  }

  // WEG-2291: the backend resolves the panel's total price mode in SQL and
  // hands it back as `calculatedTotalPrice`, so the mode is not re-derived
  // here - picking a column locally is how this cell came to disagree with
  // the exports and the API. The fall back to the plain price stays, since it
  // also covers a backend that predates this change.
  const totalPriceDisplay =
    isTotalPriceAvailable(mePreferencesData?.monitoring_source) &&
    mePreferencesData?.total_price
      ? tracker?.calculatedTotalPrice || tracker?.calculatedPrice
      : null

  // On a hybrid panel the figure means different things per row, so the rows
  // with no feed behind them say so.
  const showEngineFallbackHint =
    mePreferencesData?.monitoring_source === 'HYBRID' &&
    tracker?.isChannelBased !== '1'

  const PositionCalculator = () => {
    let range = position?.maxPrice?.value - position?.myPrice
    if (position?.myPrice - position?.minPrice?.value > range) {
      range = position?.myPrice - position?.minPrice?.value
    }
    if (range === 0) {
      range = 0.0001
    }
    const diff = Math.round(position?.myPrice - tracker?.calculatedPrice)

    let percentage = (Math.abs(diff) / range) * 100
    if (percentage > 100) {
      percentage = 100
    }

    const red = diff > 0
    const positionChange = red ? 'lower' : 'higher'
    const positionText =
      Math.abs(diff) + ' ' + meData?.currency_name + ' ' + positionChange

    return tracker.urlStatus === 1 &&
      !isNaN(Number(position?.myPrice)) &&
      position?.myPrice != null &&
      !isNaN(Number(tracker?.calculatedPrice)) ? (
      <div className="flex flex-row items-center justify-center">
        <Tooltip text={positionText}>
          <div className="relative ">
            <div className="flex flex-row items-center justify-center">
              <div
                className={`w-6 h-2 rounded-lg  ${
                  tracker?.is_mine ? 'bg-gray-25' : 'bg-gray-200'
                }`}
                style={{ borderRadius: '4px 0px 0px 4px' }}
              >
                <div
                  className={`absolute bg-error-600 h-2 right-1/2 top-0 bottom-0 rounded-l-md`}
                  style={{ left: red ? 50 - percentage / 2 + '%' : '' }}
                ></div>

                <div
                  className={`absolute bg-success-600 h-2 left-1/2 top-0 bottom-0 rounded-r-md`}
                  style={{ right: !red ? 50 - percentage / 2 + '%' : '' }}
                ></div>
              </div>
              <div
                className={`w-6 h-2 rounded-lg  ${
                  tracker?.is_mine ? 'bg-gray-25' : 'bg-gray-200'
                }`}
                style={{ borderRadius: '0px 4px 4px 0px' }}
              ></div>
              <div
                className="absolute h-5 bg-gray-500 rounded-sm"
                style={{ width: '2px' }}
              ></div>
            </div>
          </div>
        </Tooltip>
      </div>
    ) : tracker.urlStatus === 0 || tracker.urlStatus === 1500 ? (
      <div className="flex flex-row items-center justify-center">
        <Clock />
      </div>
    ) : (
      <div className="flex flex-row items-center justify-center">-</div>
    )
  }

  return (
    <>
      <tr
        className={`hover:bg-primary-25 border-b border-solid border-gray-200 ${
          tracker?.is_mine
            ? 'bg-yellow-100'
            : tracker?.is_paused
              ? 'opacity-70 bg-gray-100'
              : new URLSearchParams(window.location.search)
                    .get('company')
                    ?.split(',')
                    .filter((id) => !id.startsWith('-'))
                    .map(Number)
                    .includes(tracker?.site_id)
                ? 'bg-success-25'
                : ''
        }`}
        key={tracker?.id}
      >
        {isEditing === tracker.id ? (
          <td
            colSpan={8}
            className="px-6 py-3 font-medium text-sm text-gray-900"
          >
            <div className="flex flex-row justify-between">
              {tracker?.url ? (
                <TextInput
                  defaultValue={getDecodedURL(tracker?.url)}
                  onChange={(e) => {
                    setNewValue(e.target.value)
                  }}
                />
              ) : (
                <></>
              )}
              <PrimaryButton
                bgColor="100"
                classNameText="!text-sm"
                classNameButton="ml-2 px-4 py-1 "
                onClick={onUpdate}
              >
                Save
              </PrimaryButton>
              <PrimaryButton
                classNameText="!text-sm"
                classNameButton="ml-2 px-4 py-1 text-sm"
                onClick={() => {
                  setEditing(null)
                }}
              >
                Cancel
              </PrimaryButton>
            </div>
          </td>
        ) : (
          <>
            <td className="pl-6 py-3 font-medium text-sm text-gray-900">
              {tracker ? (
                <div className="flex flex-row">
                  {isDebug ? (
                    <Tooltip text={debugTracker} isDebug={isDebug}>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(debugTracker)
                        }}
                      >
                        [{tracker.urlStatus}]
                      </button>
                    </Tooltip>
                  ) : (
                    ''
                  )}

                  {tracker?.url ? (
                    <a
                      target="_blank"
                      rel="noreferrer noopener"
                      href={getDecodedURL(tracker.url)}
                      className="flex row items-center"
                    >
                      {mePreferencesData?.monitoring_source !== 'PRISYNC_ENGINE'
                        ? tracker?.siteName
                        : tracker?.siteUrl}{' '}
                      {tracker?.is_mine ? <Flag className="ml-2" /> : null}
                    </a>
                  ) : (
                    <></>
                  )}
                </div>
              ) : (
                '-'
              )}
            </td>
            <td className="font-medium px-1 text-sm text-gray-700 text-center">
              {tracker && tracker?.urlStatus === 1
                ? parseFloat(tracker?.calculatedPrice).toFixed(2)
                : '-'}
            </td>

            {showTotalPrice ? (
              <td className="px-1 text-center">
                {tracker && tracker?.urlStatus === 1 && totalPriceDisplay ? (
                  <div className="flex justify-center items-center space-x-1 font-medium text-sm text-gray-700">
                    <span>{parseFloat(totalPriceDisplay).toFixed(2)}</span>
                    {showEngineFallbackHint && (
                      <Tooltip text={ENGINE_URL_FALLBACK_NOTE}>
                        <HelpIcon />
                      </Tooltip>
                    )}
                  </div>
                ) : (
                  '-'
                )}
              </td>
            ) : (
              <></>
            )}

            <td className="px-1 text-center py-3">
              {tracker?.urlStatus === 1 ? (
                <>
                  {tracker.urlChangeDay > 0 ? (
                    <Tooltip
                      text={`Yesterday: ${
                        parseFloat(tracker.calculatedYesterdayPrice).toFixed(
                          2,
                        ) ?? 0.0
                      } ${meData?.currency_name}`}
                    >
                      <div className="bg-success-50 border rounded-2xl p-2 w-10 flex justify-center">
                        <ArrowUp />
                      </div>
                    </Tooltip>
                  ) : tracker.urlChangeDay < 0 ? (
                    <Tooltip
                      text={`Yesterday: ${
                        parseFloat(tracker.calculatedYesterdayPrice).toFixed(
                          2,
                        ) ?? 0.0
                      } ${meData?.currency_name}`}
                    >
                      <div className="bg-error-50 border rounded-2xl p-2 w-10 flex justify-center">
                        <ArrowRed />
                      </div>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      text={`Yesterday: ${
                        parseFloat(tracker.calculatedYesterdayPrice).toFixed(
                          2,
                        ) ?? 0.0
                      } ${meData?.currency_name}`}
                    >
                      -
                    </Tooltip>
                  )}
                </>
              ) : tracker.urlStatus === UNQUEUED_URL_CONTROLLING_STATUS ||
                tracker.urlStatus === NEW_URL_CONTROLLING_STATUS ? (
                <div className="flex justify-center items-center">
                  <Clock />
                </div>
              ) : (
                '-'
              )}
            </td>
            <td className="px-1 text-center">
              {tracker.urlStatus === 1 && position.myPrice !== 0 ? (
                <PositionCalculator />
              ) : (
                '-'
              )}
            </td>
            <td className="px-1 py-3">
              <div className="flex justify-center items-center">
                {tracker.urlStatus === 0 || tracker.urlStatus === 1500 ? (
                  '-'
                ) : !(tracker.urlStatus === 1 || tracker.urlStatus === 3000) ? (
                  '-'
                ) : tracker.is_mine && meData.type > 0 ? (
                  '-'
                ) : tracker.urlInStock === 0 ? (
                  <div className="bg-error-50 border rounded-2xl p-2 w-10 flex justify-center">
                    <XRed />
                  </div>
                ) : tracker.urlInStock === 1 ? (
                  <div className="bg-success-50 border rounded-2xl p-2 w-10 flex justify-center">
                    <TickGreen />
                  </div>
                ) : (
                  '-'
                )}
              </div>
            </td>
            <td className="font-medium text-sm text-gray-700 px-1">
              {tracker.urlStatus === 1 ? (
                tracker.is_mine ? (
                  <p>
                    {calculateIsmineUrlDateUpdated(
                      tracker.urlDateUpdated,
                      tracker.urlDateCreated,
                    )}
                  </p>
                ) : (
                  tracker.urlDateUpdated !== null && (
                    <p>
                      {DateTime.fromISO(tracker.urlDateUpdated).toRelative()}
                    </p>
                  )
                )
              ) : tracker.urlStatus === 404 ? (
                <div className="flex flex-row space-x-1 items-center">
                  <p>This page does not exist.</p>
                </div>
              ) : tracker.urlStatus === 3000 ? (
                <div className="flex flex-row space-x-1 items-center">
                  <p>Unable to retrieve the price</p>
                  <Tooltip text="We are unable to find a price on this link.">
                    <HelpIcon />
                  </Tooltip>
                </div>
              ) : tracker.urlStatus === 1500 ? (
                <div className="flex flex-row space-x-1">
                  <p>Discovering the website</p>
                  <Tooltip text="This is a website that hasn't been covered yet; now, we are processing it. It will take up to 24 hours.">
                    <HelpIcon />
                  </Tooltip>
                </div>
              ) : tracker.urlStatus === 0 ? (
                <div className="flex flex-row space-x-1">
                  <p>Will be updated in a few hours</p>
                  <Tooltip text="The link may be broken or the website may be temporarily down. If you think the link is correct and the website is healthy, please report the link via the warning icon on the right.">
                    <HelpIcon />
                  </Tooltip>
                </div>
              ) : (
                // Default content when urlStatus is none of the above
                <div className="flex flex-row space-x-1">
                  <p>Unable to retrieve the link </p>
                  <Tooltip text="The link may be broken or the website may be temporarily down. If you think the link is correct and the website is healthy, please report the link via the warning icon on the right.">
                    <HelpIcon />
                  </Tooltip>
                </div>
              )}
            </td>
            <td className="px-1">
              {isSuccess ? (
                <>
                  {tracker.isChannelBased !== '1' || tracker.is_mine ? (
                    <div className="flex flex-row space-x-1 items-center justify-center">
                      <Tooltip text="Edit URL">
                        <button
                          type="button"
                          onClick={() => setEditing(tracker.id)}
                        >
                          <Pencil />
                        </button>
                      </Tooltip>
                      <Tooltip
                        text={
                          tracker.urlStatus === 0 || tracker.urlStatus === 1500
                            ? 'We are on top of it!'
                            : 'Report URL'
                        }
                      >
                        {tracker.urlStatus === 0 ||
                        tracker.urlStatus === 1500 ? (
                          <div className="opacity-75">
                            <Alert />
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              reportStatus(createRequest)
                            }}
                            type="button"
                          >
                            <Alert />
                          </button>
                        )}
                      </Tooltip>
                      {!isShopifyOwnUrl && (
                        <Tooltip text="Remove URL">
                          <button onClick={handleRemoveUrlClick} type="button">
                            <Rubbish />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  ) : (
                    competitorPreferencesData?.can_pause_competitor && (
                      <div className="flex flex-row space-x-1 items-center justify-center">
                        <Tooltip
                          text={
                            tracker.is_paused ? 'Remove pause URL' : 'Pause URL'
                          }
                        >
                          <button
                            type="button"
                            onClick={async () => {
                              if (tracker.is_paused) {
                                await toggleExclusion(tracker.id)
                              } else {
                                openToggleExclusionModal()
                                onToggleExclusionConfirm(async () => {
                                  await toggleExclusion(tracker.id)
                                })
                              }
                            }}
                          >
                            {tracker.is_paused ? <PlayIcon /> : <PauseIcon />}
                          </button>
                        </Tooltip>
                      </div>
                    )
                  )}
                </>
              ) : null}
            </td>
            {additionalCost && (
              <td className="font-medium text-sm text-gray-700">
                {additionalCost}
              </td>
            )}
          </>
        )}
      </tr>
    </>
  )
}
